import type { ReactNode } from 'react';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import posthogLib from 'posthog-js';

import httpInstances from '@/api/axiosInstance';
import type { Conversation } from '@/features/AIChat/types/chat';
import { detectEnvironment } from '@/utils/detectEnvironment';
import { getValueOrDefault } from '@/utils/getValueOrDefault';
import { getGxgoConfigApiUrl } from '@/utils/gxgoConfigUrl';

const CONFIG_NAME = 'userConfig';
export type Option = {
  label: string;
  value: string;
  info?: ReactNode;
};
export type UserConfig = {
  timezone: Option;
  dateFormat: Option;
  defaultMarket: Option;
  homepage: Option;
  chatHistory: Conversation[];
};

type UserConfigState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled';
  error: string | null;
  userConfig: UserConfig;
};

type ErrorResponse = {
  message: string;
};
type Request = {
  userConfig: UserConfig;
};

const initialState: UserConfigState = {
  status: 'idle',
  error: null,
  userConfig: {
    timezone: { label: 'UTC', value: 'UTC' },
    dateFormat: { label: '14-Mar-25', value: 'dd-MMM-yy' },
    defaultMarket: { label: '', value: '' },
    homepage: { label: '', value: '' },
    chatHistory: []
  }
};

const parseUserConfigResponse = (data: UserConfig & { defaultInsight?: Option }) => {
  const defaultMarket = data.defaultMarket ?? data.defaultInsight;
  const homepage = data?.homepage?.value?.length ? data.homepage : defaultMarket;
  const chatHistory = data?.chatHistory ?? [];
  return { ...data, defaultMarket, homepage, chatHistory };
};

/**
 * Fetches user config from the API with migration support.
 * On staging, if the staging endpoint returns 404, falls back to the
 * non-staging endpoint and migrates the config to staging.
 */
export const fetchUserConfig = async () => {
  try {
    const { data } = await httpInstances.axiosInternalInstance.get(
      `${getGxgoConfigApiUrl()}/userConfig`
    );
    return parseUserConfigResponse(data);
  } catch (error) {
    // If staging endpoint returns 404, try the non-staging endpoint for migration
    // Remove this fallback once all staging users are migrated.
    if (error instanceof AxiosError && error.response?.status === 404) {
      const { data } = await httpInstances.axiosInternalInstance.get('/config/gxgo/userConfig');
      // Migrate user config to staging endpoint
      const temp = { [CONFIG_NAME]: data };
      await httpInstances.axiosInternalInstance.post(getGxgoConfigApiUrl(), temp);
      return parseUserConfigResponse(data);
    }
    throw error;
  }
};

export const getUserConfig = createAsyncThunk('getUserConfig', async (_, { rejectWithValue }) => {
  try {
    return await fetchUserConfig();
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      return rejectWithValue(error.response.data);
    }
    throw error;
  }
});

export const saveUserConfig = createAsyncThunk(
  'saveUserConfig',
  async (request: Request, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const userEmail = state.tripToken.userEmail;
      const previousHomepage = state.userConfig.userConfig.homepage.value;
      const responses = await postUserConfig(request, userEmail, previousHomepage);

      if (responses.every(r => r.status === 200)) {
        posthogLib.capture('$set', {
          $set: {
            defaultMarket: request.userConfig.defaultMarket.label,
            homepage: request.userConfig.homepage.label
          }
        });
        return request.userConfig;
      }
      return rejectWithValue("Couldn't save config file");
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);
const userConfigSlice = createSlice({
  name: CONFIG_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getUserConfig.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getUserConfig.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.userConfig = { ...state.userConfig, ...action.payload };
      })
      .addCase(getUserConfig.rejected, (state, action) => {
        state.status = 'failed';
        if (action.payload) {
          state.error = (action.payload as ErrorResponse).message || 'Request failed';
        } else {
          state.error = getValueOrDefault(action.error.message, 'Request failed');
        }
      })
      .addCase(saveUserConfig.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.userConfig = { ...state.userConfig, ...action.payload };
      })
      .addCase(saveUserConfig.rejected, (state, action) => {
        state.status = 'failed';
        if (action.payload) {
          state.error = (action.payload as ErrorResponse).message || 'Request failed';
        } else {
          state.error = getValueOrDefault(action.error.message, 'Request failed');
        }
      });
  }
});

export default userConfigSlice.reducer;

const postUserConfig = (request: Request, userEmail: string, previousHomepage: string) => {
  const temp = { [CONFIG_NAME]: request.userConfig };
  const newHomepage = request.userConfig.homepage.value;

  const postResponsePromise = httpInstances.axiosInternalInstance.post(getGxgoConfigApiUrl(), temp);

  if (newHomepage !== previousHomepage) {
    const prefMarket = detectEnvironment() === 'staging' ? `${newHomepage}_staging` : newHomepage;
    const patchResponsePromise = httpInstances.axiosGoInstance.patch(`/users/${userEmail}`, {
      prefMarket
    });
    return Promise.all([postResponsePromise, patchResponsePromise]);
  }

  return Promise.all([postResponsePromise]);
};
